import type { AppState, GameState, PhotoData, PhotoResults } from '../types/game';

export type GameAction =
  | { type: 'SET_FIELD'; field: keyof AppState; value: unknown }
  | { type: 'JOIN_SUCCESS'; playerId: string; roomCode: string; authToken: string; isHost: boolean; playerName: string }
  | { type: 'UPDATE_GAME_STATE'; state: GameState }
  | { type: 'NEW_PHOTO'; photo: PhotoData }
  | { type: 'PHOTO_RESULTS'; results: PhotoResults }
  | { type: 'GAME_FINISHED'; leaderboard: PhotoResults['leaderboard'] }
  | { type: 'GAME_RESET'; state: GameState }
  | { type: 'ADD_UPLOADED_PHOTO'; url: string }
  | { type: 'RESET_TO_HOME' }
  | { type: 'SHOW_SUCCESS'; message: string }
  | { type: 'SHOW_ERROR'; message: string }
  | { type: 'CLEAR_MESSAGES' };

export const initialState: AppState = {
  socket: null,
  currentView: 'home',
  authToken: '',
  playerName: '',
  playerId: '',
  isHost: false,
  roomCode: '',
  gameState: {
    gameState: 'waiting',
    players: [],
    totalPhotos: 0,
    canStartGame: false,
  },
  currentPhoto: null,
  selectedPlayer: '',
  photoResults: null,
  finalLeaderboard: null,
  timeRemaining: 30,
  hasSubmittedGuess: false,
  resultsCountdown: 5,
  previousScores: new Map(),
  showConfetti: false,
  uploadedPhotos: [],
  uploadProgress: 0,
  uploadMode: 'manual',
  error: '',
  success: '',
  loading: false,
  showPlayersList: false,
  showTutorial: false,
  showPrivacyPopup: false,
};

export function gameReducer(state: AppState, action: GameAction): AppState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };

    case 'JOIN_SUCCESS':
      return {
        ...state,
        playerId: action.playerId,
        roomCode: action.roomCode,
        authToken: action.authToken,
        isHost: action.isHost,
        playerName: action.playerName,
        currentView: 'waiting',
      };

    case 'UPDATE_GAME_STATE': {
      const newIsHost = action.state.hostId
        ? state.playerId === action.state.hostId
        : state.isHost;
      let newView = state.currentView;
      if (action.state.gameState === 'waiting') newView = 'waiting';
      else if (action.state.gameState === 'playing') newView = 'game';
      // Don't switch to results here — wait for GAME_FINISHED which brings the leaderboard
      else if (action.state.gameState === 'finished' && state.finalLeaderboard) newView = 'results';
      return { ...state, gameState: action.state, isHost: newIsHost, currentView: newView };
    }

    case 'NEW_PHOTO': {
      // Save current round's scores as previousScores for next round's +delta display
      const newPrevScores = new Map<string, number>();
      if (state.photoResults?.leaderboard) {
        state.photoResults.leaderboard.forEach(p => newPrevScores.set(p.id, p.score));
      }
      return {
        ...state,
        previousScores: newPrevScores.size > 0 ? newPrevScores : state.previousScores,
        currentPhoto: action.photo,
        selectedPlayer: '',
        photoResults: null,
        currentView: 'game',
        timeRemaining: 30,
        hasSubmittedGuess: false,
        resultsCountdown: 5,
      };
    }

    case 'PHOTO_RESULTS':
      return {
        ...state,
        photoResults: action.results,
        resultsCountdown: 5,
      };

    case 'GAME_FINISHED':
      return {
        ...state,
        currentView: 'results',
        finalLeaderboard: action.leaderboard,
        photoResults: null,
        gameState: { ...state.gameState, gameState: 'finished' },
      };

    case 'GAME_RESET': {
      const resetIsHost = action.state.hostId
        ? state.playerId === action.state.hostId
        : state.isHost;
      return {
        ...state,
        currentView: 'waiting',
        gameState: action.state,
        isHost: resetIsHost,
        currentPhoto: null,
        selectedPlayer: '',
        photoResults: null,
        finalLeaderboard: null,
        previousScores: new Map(),
        uploadedPhotos: [],
      };
    }

    case 'ADD_UPLOADED_PHOTO':
      return { ...state, uploadedPhotos: [...state.uploadedPhotos, action.url] };

    case 'RESET_TO_HOME':
      return {
        ...initialState,
        showTutorial: state.showTutorial,
        showPrivacyPopup: state.showPrivacyPopup,
      };

    case 'SHOW_SUCCESS':
      return { ...state, success: action.message };

    case 'SHOW_ERROR':
      return { ...state, error: action.message };

    case 'CLEAR_MESSAGES':
      return { ...state, error: '', success: '' };

    default:
      return state;
  }
}
